import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from 'hooks/useWorkspace';

import arrow from 'assets/arrow.svg';

import FilledState from 'Pages/TeamDashboard/ProductLibrary/components/FilledState';
import LoadingState from 'Pages/TeamDashboard/ProductLibrary/components/LoadingState';

import { fetchProductLibraryAction } from 'Redux/Actions/productLibraryActions';

import { ROUTES } from 'routes/route.constants';

import styles from './styles.module.scss';

const ProductLibrarySelection = ({ onBack, onConfirm, selectedLibraryFlowUuid, onLibraryFlowSelect }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedWorkspace } = useWorkspace();
  const { categories, isLoading, isEmpty } = useSelector(state => state.productLibrary);

  const [selectedVersions, setSelectedVersions] = useState({});

  useEffect(() => {
    dispatch(fetchProductLibraryAction(selectedWorkspace?.uuid));
  }, [selectedWorkspace?.uuid, dispatch]);

  const selectedFlow = useMemo(() => {
    if (!selectedLibraryFlowUuid) {
      return null;
    }

    for (const category of categories) {
      const found = category.flows.find(f => f.uuid === selectedLibraryFlowUuid);

      if (found) {
        return found;
      }
    }

    return null;
  }, [categories, selectedLibraryFlowUuid]);

  const handleConfirm = useCallback(() => {
    if (selectedFlow) {
      // Get the selected version for this flow, or use current_version
      const selectedVersion = selectedVersions[selectedFlow.uuid] || selectedFlow.current_version;

      // Pass flow with selected version info
      onConfirm({
        ...selectedFlow,
        selected_version: selectedVersion,
      });
    }
  }, [onConfirm, selectedFlow, selectedVersions]);

  const handleVersionSelect = useCallback((flowUuid, versionNumber) => {
    setSelectedVersions(prev => ({
      ...prev,
      [flowUuid]: versionNumber,
    }));
  }, []);

  const handleGoToProductLibrary = useCallback(() => {
    // Set flag to open product library when arriving at dashboard
    localStorage.setItem('openProductLibrary', 'true');
    navigate(ROUTES.TEAM_HOME);
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {isLoading && <LoadingState />}

        {!isLoading && isEmpty && (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              Your product library is empty. Add flows first.
            </p>
            <button
              type="button"
              className={styles.goToLibraryBtn}
              onClick={handleGoToProductLibrary}
            >
              Go to Product Library
            </button>
          </div>
        )}

        {!isLoading && !isEmpty && (
          <FilledState
            categories={categories}
            selectionMode
            selectedLibraryFlowUuid={selectedLibraryFlowUuid}
            onLibraryFlowSelect={onLibraryFlowSelect}
            screenCount={selectedFlow?.screen_count ?? 0}
            onVersionSelectExternal={handleVersionSelect}
            selectedVersionsExternal={selectedVersions}
          />
        )}
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className={styles.confirmBtn}
          onClick={handleConfirm}
          disabled={!selectedFlow}
        >
          Add your context
          <img src={arrow} alt="" />
        </button>
      </div>
    </div>
  );
};

ProductLibrarySelection.propTypes = {
  onBack: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedLibraryFlowUuid: PropTypes.string,
  onLibraryFlowSelect: PropTypes.func.isRequired,
};

ProductLibrarySelection.defaultProps = {
  selectedLibraryFlowUuid: null,
};

export default ProductLibrarySelection;
